// A small modal for creating a record from a few fields. The rest are edited in the side panel.
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Button, Input, Select } from "./controls.tsx";
import { DialogLayer } from "./DialogLayer.tsx";
import type { FieldDef } from "./RecordPanel.tsx";
import { SkuInput } from "./SkuInput.tsx";

type Values = Record<string, string>;

export function CreateDialog({
  title,
  fields,
  initial = {},
  onSubmit,
  onClose,
}: {
  title: string;
  fields: FieldDef[];
  initial?: Values;
  /** Return true to close. */
  onSubmit: (values: Values) => Promise<boolean>;
  onClose: () => void;
}) {
  // A select's choices, static or following the other values (a New part's Category follows its Type).
  const optionsOf = (f: FieldDef, values: Values) => (f.optionsFor ? f.optionsFor(values) : (f.options ?? []));
  /** A select whose choices follow the other values holds one of them (in order, so it sees an earlier field's new
   * value): a value that isn't among them becomes the first, so switching a part's Type resets its Category. */
  const settle = (values: Values): Values => {
    const out = { ...values };
    for (const f of fields) {
      if (f.type !== "select" || !f.optionsFor) continue;
      const opts = f.optionsFor(out);
      if (opts.length && !opts.some((o) => o.value === out[f.key])) out[f.key] = opts[0].value;
    }
    return out;
  };
  const [values, setValues] = useState<Values>(() =>
    settle({
      ...Object.fromEntries(fields.map((f) => [f.key, f.type === "select" && !f.optionsFor ? (f.options?.[0]?.value ?? "") : ""])),
      ...initial,
    }),
  );
  const set = (key: string, value: string) => setValues(settle({ ...values, [key]: value }));
  const [busy, setBusy] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  useEffect(() => first.current?.focus(), []);
  // Part SKUs --- a field with a prefix holds only the text after it; the prefix follows the other values (a part's Type)
  const prefixOf = (f: FieldDef) => (typeof f.prefix === "function" ? f.prefix(values) : (f.prefix ?? ""));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const ok = await onSubmit({ ...values, ...Object.fromEntries(fields.filter((f) => f.prefix).map((f) => [f.key, prefixOf(f) + values[f.key].trim()])) });
    setBusy(false);
    if (ok) onClose();
  };

  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form
        onSubmit={submit}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            onClose();
          }
        }}
        className="w-[420px] rounded-xl border border-line bg-surface p-4 shadow-pop"
      >
        <div className="mb-3 text-[15px] font-semibold">{title}</div>
        <div className="flex flex-col gap-3">
          {fields.map((f, i) => {
            const opts = f.type === "select" ? optionsOf(f, values) : [];
            // A select whose choices follow the other values and has none now isn't asked (a service has no Category).
            if (f.optionsFor && !opts.length) return null;
            return (
              <label key={f.key} className="flex flex-col gap-1">
                <span className="text-[12px] text-muted">{f.label}</span>
                {f.type === "select" ? (
                  <Select value={values[f.key]} options={opts} onChange={(e) => set(f.key, e.target.value)} />
                ) : f.prefix ? (
                  <SkuInput
                    ref={i === 0 ? first : undefined}
                    prefix={prefixOf(f)}
                    value={values[f.key]}
                    placeholder={f.placeholder}
                    onChange={(rest) => set(f.key, rest)}
                  />
                ) : (
                  <Input
                    ref={i === 0 ? first : undefined}
                    type={f.type === "date" ? "date" : "text"}
                    inputMode={f.type === "number" ? "decimal" : undefined}
                    value={values[f.key]}
                    placeholder={f.placeholder}
                    onChange={(e) => set(f.key, e.target.value)}
                  />
                )}
              </label>
            );
          })}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={busy} kbd="↵">
            Create
          </Button>
        </div>
      </form>
    </DialogLayer>
  );
}
